import { Head, Link } from "@inertiajs/react";
import {
  AspectRatio,
  Box,
  Button,
  Divider,
  Group,
  Image,
  Stack,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconCalendar,
  IconClock,
  IconPlayerPlay,
  IconMicrophone,
} from "@tabler/icons-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageShell from "../Components/Layout/PageShell";
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  DimmedText,
  MainText,
  Eyebrow,
} from "../Components/UI/Typography";
import { Badge } from "../Components/UI/Badge";
import type { Post, PillarSlug, PostFormat } from "../data/posts";
import {
  articleBodyFor,
  captivateEmbedUrl,
  formatDuration,
  youtubeEmbedUrl,
  FORMAT_ORDER,
} from "../data/posts";

interface Props {
  post: Post;
}

// Pretty label for the "Back to X" link + pillar breadcrumb.
const PILLAR_LABELS: Record<PillarSlug, string> = {
  tech: "Tech",
  dance: "Dance",
  media: "Media",
  think: "Think",
};

function pillarHref(pillar: PillarSlug): string {
  return `/${pillar}`;
}

function findFormat<T extends PostFormat["type"]>(
  post: Post,
  type: T,
): Extract<PostFormat, { type: T }> | undefined {
  return post.formats.find(
    (f): f is Extract<PostFormat, { type: T }> => f.type === type,
  );
}

export default function PostDetail({ post }: Props) {
  const formattedDate = new Date(post.publishedAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const pillarLabel = PILLAR_LABELS[post.pillar];
  const siteUrl = import.meta.env.VITE_SITE_URL || "";
  const ogImage = post.coverImage || import.meta.env.VITE_OG_IMAGE_URL || "";

  return (
    <>
      <Head>
        <title>{`${post.title} | Ogarocious`}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={`${post.title} | Ogarocious`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${siteUrl}/${post.pillar}/${post.slug}`} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} | Ogarocious`} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={ogImage} />
      </Head>

      <PageShell variant="content" containerSize="md">
        <Stack gap={40}>
          {/* ── Back ── */}
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            component={Link}
            href={pillarHref(post.pillar)}
            style={{
              color: "rgba(161, 161, 170, 0.7)",
              alignSelf: "flex-start",
            }}
          >
            Back to {pillarLabel}
          </Button>

          {/* ── Cover image (optional) ── */}
          {post.coverImage && (
            <Image
              src={post.coverImage}
              alt={post.title}
              radius="md"
              style={{ display: "block" }}
            />
          )}

          {/* ── Post Header ── */}
          <Stack gap="md">
            <Group gap="xs" wrap="wrap">
              <Badge variant="outline" size="sm" radius="md">
                {pillarLabel}
              </Badge>
              {post.tags.map((tag) => (
                <Badge key={tag} variant="light" size="sm" radius="md">
                  {tag}
                </Badge>
              ))}
            </Group>

            <Heading1 variant="shadow">{post.title}</Heading1>

            <MainText
              size="lg"
              style={{
                lineHeight: 1.6,
                color: "rgba(255,255,255,0.75)",
              }}
            >
              {post.excerpt}
            </MainText>

            <Group gap="lg">
              <Group gap="xs">
                <IconCalendar
                  size={15}
                  style={{ color: "rgba(161, 161, 170, 0.5)" }}
                />
                <DimmedText size="sm">{formattedDate}</DimmedText>
              </Group>
              {post.formats.map((f) => (
                <FormatMeta key={f.type} format={f} />
              ))}
            </Group>
          </Stack>

          <Divider color="rgba(55, 58, 64, 0.4)" />

          {/* ── Format sections, in fixed order Video → Podcast → Article ── */}
          {FORMAT_ORDER.map((type) => {
            if (type === "video") {
              const video = findFormat(post, "video");
              return video ? (
                <VideoSection key={type} format={video} title={post.title} />
              ) : null;
            }
            if (type === "podcast") {
              const podcast = findFormat(post, "podcast");
              return podcast ? (
                <PodcastSection key={type} format={podcast} title={post.title} />
              ) : null;
            }
            if (type === "article") {
              const article = findFormat(post, "article");
              const body = articleBodyFor(post);
              return article && body ? (
                <ArticleSection key={type} body={body} />
              ) : null;
            }
            return null;
          })}

          <Divider color="rgba(55, 58, 64, 0.4)" />

          {/* ── Footer nav ── */}
          <Group justify="space-between">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              component={Link}
              href={pillarHref(post.pillar)}
            >
              Back to {pillarLabel}
            </Button>
            <Button variant="gradient" component={Link} href="/contact">
              Work With Me
            </Button>
          </Group>
        </Stack>
      </PageShell>
    </>
  );
}

// ── Sections ───────────────────────────────────────────────────────────────

function FormatMeta({ format }: { format: PostFormat }) {
  if (format.type === "article") {
    return (
      <Group gap="xs">
        <IconClock size={15} style={{ color: "rgba(161, 161, 170, 0.5)" }} />
        <DimmedText size="sm">{format.readTimeMin} min read</DimmedText>
      </Group>
    );
  }
  if (format.type === "video") {
    return (
      <Group gap="xs">
        <IconPlayerPlay
          size={15}
          style={{ color: "rgba(161, 161, 170, 0.5)" }}
        />
        <DimmedText size="sm">{formatDuration(format.durationSec)}</DimmedText>
      </Group>
    );
  }
  return (
    <Group gap="xs">
      <IconMicrophone
        size={15}
        style={{ color: "rgba(161, 161, 170, 0.5)" }}
      />
      <DimmedText size="sm">{formatDuration(format.durationSec)}</DimmedText>
    </Group>
  );
}

function VideoSection({
  format,
  title,
}: {
  format: Extract<PostFormat, { type: "video" }>;
  title: string;
}) {
  return (
    <Stack gap="sm">
      <Eyebrow>Watch</Eyebrow>
      <AspectRatio ratio={16 / 9}>
        <iframe
          src={youtubeEmbedUrl(format.videoId)}
          title={title}
          style={{
            border: "none",
            borderRadius: 8,
            width: "100%",
            height: "100%",
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </AspectRatio>
    </Stack>
  );
}

function PodcastSection({
  format,
  title,
}: {
  format: Extract<PostFormat, { type: "podcast" }>;
  title: string;
}) {
  return (
    <Stack gap="sm">
      <Eyebrow>Listen</Eyebrow>
      <Box
        style={{
          borderRadius: 8,
          overflow: "hidden",
          background: "rgba(18,18,18,0.55)",
          border: "1px solid rgba(212,85,43,0.12)",
        }}
      >
        <iframe
          src={captivateEmbedUrl(format.episodeId)}
          title={title}
          style={{
            border: "none",
            width: "100%",
            height: 200,
            display: "block",
          }}
          scrolling="no"
        />
      </Box>
    </Stack>
  );
}

function ArticleSection({ body }: { body: string }) {
  return (
    <Stack gap="sm">
      <Eyebrow>Read</Eyebrow>
      <Box
        style={{
          color: "rgba(255, 255, 255, 0.88)",
          lineHeight: 1.8,
          fontSize: "clamp(0.9rem, 2.5vw, 1.0625rem)",
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <Heading1 style={{ marginTop: "2rem", marginBottom: "1rem" }}>
                {children}
              </Heading1>
            ),
            h2: ({ children }) => (
              <Heading2 style={{ marginTop: "2rem", marginBottom: "0.75rem" }}>
                {children}
              </Heading2>
            ),
            h3: ({ children }) => (
              <Heading3 style={{ marginTop: "1.5rem", marginBottom: "0.5rem" }}>
                {children}
              </Heading3>
            ),
            h4: ({ children }) => (
              <Heading4 style={{ marginTop: "1.25rem", marginBottom: "0.5rem" }}>
                {children}
              </Heading4>
            ),
            p: ({ children }) => (
              <p
                style={{
                  color: "rgba(255, 255, 255, 0.82)",
                  lineHeight: 1.8,
                  marginBottom: "1.25rem",
                  fontSize: "clamp(0.9rem, 2.5vw, 1.0625rem)",
                }}
              >
                {children}
              </p>
            ),
            strong: ({ children }) => (
              <strong style={{ color: "#f5eee0", fontWeight: 600 }}>
                {children}
              </strong>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#d4552b", textDecoration: "underline" }}
              >
                {children}
              </a>
            ),
            ul: ({ children }) => (
              <ul
                style={{
                  color: "rgba(245, 238, 224, 0.90)",
                  paddingLeft: "1.5rem",
                  marginBottom: "1.25rem",
                  lineHeight: 1.8,
                }}
              >
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol
                style={{
                  color: "rgba(245, 238, 224, 0.90)",
                  paddingLeft: "1.5rem",
                  marginBottom: "1.25rem",
                  lineHeight: 1.8,
                }}
              >
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li style={{ marginBottom: "0.5rem" }}>{children}</li>
            ),
            blockquote: ({ children }) => (
              <blockquote
                style={{
                  borderLeft: "3px solid #d4552b",
                  paddingLeft: "1.25rem",
                  margin: "1.5rem 0",
                  color: "rgba(245, 238, 224, 0.72)",
                  fontStyle: "italic",
                }}
              >
                {children}
              </blockquote>
            ),
            code: ({ children, className }) => {
              const isBlock = className?.startsWith("language-");
              if (isBlock) {
                return (
                  <pre
                    style={{
                      background: "rgba(26, 27, 30, 0.95)",
                      border: "1px solid rgba(55, 58, 64, 0.4)",
                      borderRadius: "0.5rem",
                      padding: "clamp(0.75rem, 3vw, 1.25rem)",
                      overflowX: "auto",
                      marginBottom: "1.25rem",
                    }}
                  >
                    <code
                      style={{
                        color: "rgba(20, 184, 166, 0.9)",
                        fontSize: "0.9rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {children}
                    </code>
                  </pre>
                );
              }
              return (
                <code
                  style={{
                    background: "rgba(20, 184, 166, 0.1)",
                    color: "rgba(20, 184, 166, 0.9)",
                    padding: "0.15em 0.4em",
                    borderRadius: "0.25rem",
                    fontSize: "0.9em",
                    fontFamily: "monospace",
                  }}
                >
                  {children}
                </code>
              );
            },
            hr: () => (
              <Divider
                color="rgba(55, 58, 64, 0.4)"
                style={{ margin: "2rem 0" }}
              />
            ),
          }}
        >
          {body}
        </ReactMarkdown>
      </Box>
    </Stack>
  );
}
